import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import Header from "@/components/Header";
import { trackEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Loader2, MapPin, Package, Store, Truck } from "lucide-react";
import type { Order, OrderFulfillmentSelection } from "@shared/schema";
import { stripDemoMarker } from "@/lib/demo";
import { formatOrderStatus } from "@/lib/orderStatus";
import { useAuth } from "@/hooks/useAuth";
import Deliveries from "./Deliveries";

type BuyerOrder = Order & {
  fulfillmentSelections: OrderFulfillmentSelection[];
  deliveryScheduledFor?: string | null;
  deliveryStatus?: string | null;
};

function formatScheduledForLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const weekday = d.toLocaleDateString(undefined, { weekday: "short" });
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const hour = d.getHours();
  const window = hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
  return `${weekday}, ${date}, ${window}`;
}

export default function BuyerOrders() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const canSell = Boolean((user as any)?.canSell);

  const handleRequestCancellation = async (orderId: string) => {
    trackEvent("order_action_tap", "/orders", {
      orderId,
      action: "cancel_requested",
      surface: "buyer_orders",
    });
    try {
      const res = await fetch(`/api/orders/${orderId}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: "cancel_requested" }),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      window.location.reload();
    } catch (error: any) {
      trackEvent("order_action_fail", "/orders", {
        orderId,
        action: "cancel_requested",
        surface: "buyer_orders",
        message: String(error?.message || "").slice(0, 300),
      });
      console.error("Could not request cancellation", error);
      window.alert("Could not request cancellation. Please try again.");
    }
  };

  const PAGE_SIZE = 20;
  const [pageCount, setPageCount] = useState(1);
  const limit = pageCount * PAGE_SIZE;
  const { data: orders = [], isLoading, isFetching } = useQuery<BuyerOrder[]>({
    queryKey: ["/api/my/orders", limit],
    queryFn: async () => {
      const res = await fetch(`/api/my/orders?limit=${limit}`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
  const hasMore = orders.length >= limit;

  const search = typeof window !== "undefined" ? window.location.search : "";
  const initialTab = new URLSearchParams(search).get("tab") === "deliver" ? "deliver" : "orders";
  const [activeTab, setActiveTab] = useState(initialTab);

  if (activeTab === "deliver") {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1">
          <div className="mx-auto max-w-4xl px-4 py-4">
            <div className="flex gap-1 mb-4">
              <button
                type="button"
                onClick={() => setActiveTab("orders")}
                className="flex-1 rounded-lg border px-3 py-2.5 text-sm font-medium text-center transition border-border hover:border-primary/50"
              >
                <Package className="mx-auto mb-0.5 h-4 w-4" />
                My Orders
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("deliver")}
                className="flex-1 rounded-lg border px-3 py-2.5 text-sm font-medium text-center transition border-primary bg-primary/5 text-primary"
              >
                <Truck className="mx-auto mb-0.5 h-4 w-4" />
                Deliver & Earn
              </button>
            </div>
          </div>
          <Deliveries embedded />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="container mx-auto flex-1 px-4 py-8">
        <div className="mx-auto max-w-4xl space-y-6">
          <div className="flex gap-1 mb-2">
            <button
              type="button"
              onClick={() => setActiveTab("orders")}
              className="flex-1 rounded-lg border px-3 py-2.5 text-sm font-medium text-center transition border-primary bg-primary/5 text-primary"
            >
              <Package className="mx-auto mb-0.5 h-4 w-4" />
              My Orders
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("deliver")}
              className="flex-1 rounded-lg border px-3 py-2.5 text-sm font-medium text-center transition border-border hover:border-primary/50"
            >
              <Truck className="mx-auto mb-0.5 h-4 w-4" />
              Deliver & Earn
            </button>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-semibold" data-testid="text-buyer-orders-title">
                My Orders
              </h1>
              <p className="text-muted-foreground">
                Track current orders and revisit past pickup or delivery details.
              </p>
            </div>

            <Button variant="outline" onClick={() => setLocation("/")}>
              Continue Shopping
            </Button>
          </div>

          {canSell && (
            <button
              type="button"
              onClick={() => setLocation("/seller?tab=orders")}
              className="flex w-full items-center justify-between rounded-lg border border-dashed border-primary/40 bg-primary/5 p-3 text-left text-sm transition hover:border-primary/60 hover:bg-primary/10"
            >
              <span className="flex items-center gap-2">
                <Store className="h-4 w-4 text-primary" />
                <span>
                  Looking for orders <strong>you received</strong> as a seller?
                </span>
              </span>
              <span className="font-medium text-primary">Seller dashboard →</span>
            </button>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : orders.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center gap-3 py-14 text-center">
                <Package className="h-10 w-10 text-muted-foreground" />
                <div>
                  <p className="font-medium">No orders yet</p>
                  <p className="text-sm text-muted-foreground">
                    Your placed orders will show up here once checkout is completed.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
            {orders.map((order) => (
              <Card key={order.id}>
                <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <CardTitle>Order #{order.id.slice(0, 8).toUpperCase()}</CardTitle>
                    <CardDescription>
                      Status: {formatOrderStatus(order.status)}
                    </CardDescription>
                  </div>

                  <div className="text-sm text-muted-foreground">
                    {order.createdAt ? new Date(order.createdAt).toLocaleString() : "Recently placed"}
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  {order.deliveryScheduledFor && (
                    <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
                      <div className="flex items-center gap-2 font-medium text-primary">
                        <Truck className="h-4 w-4" />
                        Out for delivery: {formatScheduledForLabel(order.deliveryScheduledFor)}
                      </div>
                    </div>
                  )}

                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-4 w-4 text-muted-foreground" />
                      <div className="text-sm">
                        <p className="font-medium">Pickup / delivery</p>
                        <p className="text-muted-foreground">{order.pickupLocation}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <Calendar className="mt-0.5 h-4 w-4 text-muted-foreground" />
                      <div className="text-sm">
                        <p className="font-medium">Window</p>
                        <p className="text-muted-foreground">{order.pickupWindow}</p>
                      </div>
                    </div>

                    <div className="text-sm">
                      <p className="font-medium">Total</p>
                      <p className="text-muted-foreground">${Number(order.total).toFixed(2)}</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {order.fulfillmentSelections.map((selection) => (
                      <div key={selection.id} className="rounded-md bg-muted/40 p-3 text-sm">
                        <p className="font-medium">{stripDemoMarker(selection.sellerName) || selection.sellerName}</p>
                        <p className="text-muted-foreground">
                          {selection.buyerLabel} • {selection.scheduledDate} • {selection.timeWindowLabel}
                        </p>
                        {selection.dropZoneName ? (
                          <p className="text-muted-foreground">
                            {selection.dropZoneName}
                            {selection.dropZoneCity ? ` — ${selection.dropZoneCity}` : ""}
                            {selection.dropZoneState ? `, ${selection.dropZoneState}` : ""}
                          </p>
                        ) : null}
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setLocation(`/orders/${order.id}`)}
                    >
                      View Details
                    </Button>

                    {(order.status === "paid" ||
                      order.status === "preparing" ||
                      order.status === "ready_for_pickup" ||
                      order.status === "out_for_delivery") && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleRequestCancellation(order.id)}
                      >
                        Request cancellation & refund
                      </Button>
                    )}
                    {(order.status === "picked_up" || order.status === "delivered") && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleRequestCancellation(order.id)}
                      >
                        Request refund
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            {hasMore && (
              <div className="flex justify-center pt-2">
                <Button
                  variant="outline"
                  onClick={() => setPageCount((v) => v + 1)}
                  disabled={isFetching}
                  data-testid="button-load-more-orders"
                >
                  {isFetching ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading…
                    </>
                  ) : (
                    "Show more orders"
                  )}
                </Button>
              </div>
            )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
